// resources/js/Pages/Formula/components/InsertionPoint.js - REFACTORED FOR VISIBILITY
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDroppable, useDndContext } from '@dnd-kit/core';

// ✅ 1. DEFINE THE ELEMENT LIBRARY (unchanged)
const ELEMENT_LIBRARY = {
  'Basic Inputs': [
    {
      key: 'Text Field',
      icon: '📝',
      description: 'A standard text or number field.',
      template: {
        type: 'field',
        key: 'new_field',
        instruction: 'Provide a detailed description or value.',
        valueType: 'string',
      }
    },
    // Future 'Custom Dropdown' could go here
  ],
  'Data Display': [
    {
      key: 'Score (1-10)',
      icon: '💯',
      description: 'Display a numeric score with a color grade.',
      template: {
        type: 'field',
        key: 'score',
        instruction: 'Provide a score from 1 to 10.',
        valueType: 'score',
      }
    },
    {
      key: 'Importance Badge',
      icon: '⚠️',
      description: 'Display a "Low", "Medium", or "High" badge.',
      template: {
        type: 'field',
        key: 'importance',
        instruction: 'Specify the level of importance (low, medium, or high).',
        valueType: 'importance',
      }
    },
    {
      key: 'Scene Link',
      icon: '🎬',
      description: 'A link to a specific scene number.',
      template: {
        type: 'field',
        key: 'scene_number',
        instruction: 'Provide a scene number.',
        valueType: 'scene_number',
      }
    }
  ],
  'Interactive Elements': [
    {
      key: 'AI Follow-up Questions',
      icon: '🤖',
      description: 'Generate a list of clickable follow-up questions.',
      template: {
        type: 'array',
        key: 'follow_up_questions',
        // This is the key: a 'ul' array with no children is a simple list of strings
        display: 'ul',
        renderAs: 'llm_questions', // ✅ ADD THIS CRITICAL PROPERTY
        children: [],
        instruction: "Based on your analysis, provide 2-3 follow-up questions a writer might ask to improve their understanding or lead them to better changes to their script.",
      }
    }
  ],
  'Layout & Structure': [
    {
      key: 'List / Array',
      icon: '📋',
      description: 'A list of items, displayed as a table, cards, etc.',
      template: { type: 'array', key: 'new_array', display: 'ul', instruction: '', children: [] }
    },
    {
      key: 'Text Block',
      icon: '¶',
      description: 'A block of static, instructional text.',
      template: { type: 'text-block', content: 'Add your text here...' }
    },
    {
      key: 'Section',
      icon: '📦',
      description: 'A container to group related fields under a title.',
      template: { type: 'section', title: 'New Section', children: [] }
    },
    {
      key: 'Divider',
      icon: '➖',
      description: 'A visual separator line.',
      template: { type: 'divider' }
    },
    {
      key: 'Graph / Chart',
      icon: '📊',
      description: 'Display a data chart or a process diagram.',
      template: { type: 'graph', key: 'new_graph', instruction: 'Describe the desired graph (e.g., a timeline of events)' }
    }
  ]
};

// Inline CSS to override app-level z-index issues
const insertionPointStyles = `
  .insertion-dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    background: white !important;
    border: 1px solid #dee2e6 !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
    min-width: 200px !important;
    max-height: 300px !important;
    overflow-y: auto !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  .insertion-dropdown-menu .dropdown-header {
    padding: 8px 16px !important;
    margin: 0 !important;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
    color: #6c757d !important;
    background: #f8f9fa !important;
    border-bottom: 1px solid #dee2e6 !important;
  }
  .insertion-dropdown-menu .dropdown-item {
    padding: 8px 16px !important;
    margin: 0 !important;
    font-size: 0.875rem !important;
    color: #212529 !important;
    background: transparent !important;
    border: none !important;
    cursor: pointer !important;
    display: block !important;
    width: 100% !important;
    text-align: left !important;
  }
  .insertion-dropdown-menu .dropdown-item:hover {
    background: #e9ecef !important;
    color: #212529 !important;
  }
  .insertion-dropdown-menu .dropdown-divider {
    height: 0 !important;
    margin: 4px 0 !important;
    overflow: hidden !important;
    border-top: 1px solid #dee2e6 !important;
  }
  .insertion-point-active {
    z-index: 1001 !important;
    position: relative !important;
  }
  .insertion-point {
    position: relative;
  }
  .insertion-point::before {
    content: '';
    position: absolute;
    top: -15px;
    left: 0;
    right: 0;
    bottom: -15px;
    height: 30px;
    z-index: 1000;
    pointer-events: auto;
  }
`;

export default function InsertionPoint({
  parentId,
  position,
  onAddElement,
  showOnHover = true,
  className = "",
  isLast = false // ✅ New prop to ensure visibility at bottom of lists
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Get dragging state from DnD context
  const { active } = useDndContext();
  const isDragging = !!active;

  // Make this insertion point droppable
  const { isOver, setNodeRef } = useDroppable({
    id: `insertion-point-${parentId}-${position}`,
    data: {
      type: 'insertion-point',
      parentId: parentId,
      position: position,
    },
  });

  const handleSelectElement = (elementTemplate) => {
    onAddElement?.(parentId, position, elementTemplate);
    setIsOpen(false);
  };

  const handleButtonClick = (e) => {
    e.stopPropagation(); // Stop bubbling
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      });
    }
    setIsOpen(!isOpen);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && buttonRef.current && !buttonRef.current.contains(event.target)) {
        const menu = document.querySelector('.insertion-dropdown-menu');
        if (!menu || !menu.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Show drop indicator when being dropped on
  const dropIndicator = (
    <div style={{
      position: 'absolute', top: '50%', left: '0', right: '0', height: '3px',
      backgroundColor: '#0d6efd', borderRadius: '2px', marginTop: '-1.5px', zIndex: 1001,
      boxShadow: '0 0 6px rgba(13, 110, 253, 0.8)', opacity: isOver ? 1 : 0,
      transform: isOver ? 'scale(1)' : 'scale(0.95)', transition: 'all 0.1s ease',
      pointerEvents: 'none'
    }} />
  );

  // ✅ IMPORTANT: If specific min-height needed for last item, apply it via style
  const wrapperStyle = isLast ? { minHeight: '24px', marginBottom: '8px' } : {};

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: insertionPointStyles }} />
      <div
        ref={setNodeRef}
        className={`insertion-point-wrapper ${className} ${isOver ? 'drop-active' : ''}`}
        style={wrapperStyle}
      >
        {dropIndicator}

        {/* ✅ RENDER ELEMENT PALETTE BUTTON */}
        {(!isDragging) && (
          <div
            ref={buttonRef}
            className="insertion-button"
            onClick={handleButtonClick}
            title="Add element"
          >
            +
          </div>
        )}
      </div>

      {/* Portal the Element Palette menu to document.body */}
      {isOpen && createPortal(
        <div
          className="insertion-dropdown-menu"
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            transform: 'translateX(-50%)',
            zIndex: 99999,
          }}
        >
          {Object.entries(ELEMENT_LIBRARY).map(([category, elements]) => (
            <React.Fragment key={category}>
              <div className="dropdown-header">{category}</div>
              {elements.map((element) => (
                <button
                  key={element.key}
                  className="dropdown-item"
                  onClick={() => handleSelectElement(element.template)}
                  title={element.description}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    border: 'none',
                    background: 'transparent',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                >
                  <span style={{ marginRight: '8px', fontSize: '16px' }}>{element.icon}</span>
                  <div>
                    <div style={{ fontWeight: '500', color: '#212529' }}>{element.key}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                      {element.description}
                    </div>
                  </div>
                </button>
              ))}
              <hr className="dropdown-divider" />
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}